import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaGift,
  FaStar,
  FaArrowRight,
  FaWifi,
  FaCoffee,
  FaShieldAlt,
  FaSwimmingPool,
  FaCalendarAlt,
  FaTag,
  FaClock,
} from 'react-icons/fa';
import { formatPrice } from '../utils/currencyPreference';
import { getImageUrl, FALLBACK_PROPERTY_IMAGE } from '../utils/imageHelper';
import './OffersDealsShowcase.css';

const DEFAULT_PERKS = {
  rose: [
    { icon: FaWifi, label: 'Free Wi-Fi' },
    { icon: FaCoffee, label: 'Breakfast Included' },
    { icon: FaShieldAlt, label: 'Free Cancellation' },
  ],
  emerald: [
    { icon: FaSwimmingPool, label: 'Pool Access' },
    { icon: FaCoffee, label: 'Complimentary Breakfast' },
    { icon: FaCalendarAlt, label: 'Flexible Booking' },
  ],
};

const getOfferTheme = (offer, index) => {
  const badge = String(offer?.badge || offer?.discount?.badge || '').toLowerCase();
  const type = String(offer?.discount?.type || '').toLowerCase();
  if (badge.includes('early') || type === 'early_bird') return 'emerald';
  if (index % 2 === 1) return 'emerald';
  return 'rose';
};

const getBadgeLabel = (offer, theme) => {
  const badge = String(offer?.badge || offer?.discount?.badge || '').trim();
  if (badge) return badge.toUpperCase();
  return theme === 'emerald' ? 'EARLY BIRD SPECIAL' : 'LIMITED TIME OFFER';
};

const getDiscountPercent = (originalPrice, pricePerNight) => {
  const original = Number(originalPrice);
  const discounted = Number(pricePerNight);
  if (!original || !discounted || discounted >= original) return null;
  return Math.round(((original - discounted) / original) * 100);
};

export const resolveOfferPropertyId = (item) => {
  if (!item) return null;
  const raw = item.propertyId || item.property?._id || item.property?.id;
  if (!raw) return null;
  return String(raw).replace(/^host-/, '');
};

const PremiumOfferCard = ({ item, index, onOfferClick }) => {
  const theme = getOfferTheme(item, index);
  const perks = DEFAULT_PERKS[theme];
  const discountPercent = getDiscountPercent(item.originalPrice, item.pricePerNight);
  const badgeLabel = getBadgeLabel(item, theme);
  const propertyId = resolveOfferPropertyId(item);
  const isClickable = Boolean(propertyId && onOfferClick);

  const handleCardClick = () => {
    if (!isClickable) return;
    onOfferClick({ ...item, propertyId });
  };

  const handleKeyDown = (event) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  return (
    <motion.article
      className={`premium-offer-card premium-offer-card--${theme}${
        isClickable ? ' premium-offer-card--clickable' : ''
      }`}
      whileHover={isClickable ? { y: -4 } : undefined}
      transition={{ duration: 0.25 }}
      onClick={isClickable ? handleCardClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? `View offer: ${item.title}` : undefined}
    >
      <img
        className="premium-offer-card-bg"
        src={getImageUrl(item.image)}
        alt=""
        loading="lazy"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = FALLBACK_PROPERTY_IMAGE;
        }}
      />
      <div className="premium-offer-card-overlay" />

      <span className={`premium-offer-badge premium-offer-badge--${theme}`}>
        {theme === 'emerald' ? <FaClock /> : <FaTag />}
        {badgeLabel}
      </span>

      <div className="premium-offer-card-body">
        <div className="premium-offer-card-copy">
          <h3>{item.title}</h3>
          <p>{item.description || item.details || 'Exclusive savings on your next stay.'}</p>
        </div>

        <div className="premium-offer-card-bottom">
          <div className="premium-offer-pricing">
            {item.originalPrice && item.originalPrice !== item.pricePerNight ? (
              <span className="premium-offer-original">
                {formatPrice(item.originalPrice)}
              </span>
            ) : null}
            <div className="premium-offer-price-row">
              <span className="premium-offer-price">
                {formatPrice(item.pricePerNight)}
                <small>/ night</small>
              </span>
              {discountPercent ? (
                <span className={`premium-offer-discount premium-offer-discount--${theme}`}>
                  {discountPercent}% OFF
                </span>
              ) : null}
            </div>
          </div>

          <div className="premium-offer-perks">
            {perks.map(({ icon: Icon, label }) => (
              <span key={label} className="premium-offer-perk">
                <Icon aria-hidden="true" />
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <span
        className={`premium-offer-cta premium-offer-cta--${theme}`}
        aria-hidden="true"
      >
        <FaArrowRight />
      </span>
    </motion.article>
  );
};

const OffersDealsShowcase = ({
  offers = [],
  loading = false,
  onViewAll,
  onOfferClick,
  showViewAll = true,
  showHeader = true,
  limit = null,
  carousel = false,
  cardsPerSlide = 2,
  autoRotateMs = 4000,
  className = '',
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const displayOffers = carousel ? offers : (limit ? offers.slice(0, limit) : offers);

  const carouselOffers = useMemo(() => {
    if (!carousel || offers.length === 0) return offers;
    if (offers.length <= cardsPerSlide) {
      return [...offers, ...offers];
    }
    return offers;
  }, [carousel, offers, cardsPerSlide]);

  const carouselSlides = useMemo(() => {
    if (!carousel) return [];
    const slides = [];
    for (let i = 0; i < carouselOffers.length; i += cardsPerSlide) {
      slides.push(carouselOffers.slice(i, i + cardsPerSlide));
    }
    return slides;
  }, [carousel, carouselOffers, cardsPerSlide]);

  useEffect(() => {
    setActiveSlide(0);
  }, [carouselOffers.length]);

  useEffect(() => {
    if (!carousel || loading || carouselSlides.length <= 1 || isPaused) return;

    const timerId = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselSlides.length);
    }, autoRotateMs);

    return () => window.clearInterval(timerId);
  }, [carousel, loading, carouselSlides.length, autoRotateMs, isPaused]);

  return (
    <section className={`offers-deals-showcase ${className}`.trim()}>
      {showHeader ? (
        <div className="offers-deals-showcase-header">
          <div className="offers-deals-showcase-header-left">
            <div className="offers-deals-showcase-icon" aria-hidden="true">
              <FaGift />
            </div>
            <div>
              <h2 className="offers-deals-showcase-title">
                Offers <span className="offers-deals-showcase-title-accent">&amp; Deals</span>
              </h2>
              <p className="offers-deals-showcase-subtitle">
                Handpicked offers to make your stay truly memorable.
              </p>
            </div>
          </div>
          <div className="offers-deals-showcase-header-right">
            <span className="offers-deals-showcase-perks">
              <FaStar aria-hidden="true" />
              Special perks
            </span>
            {showViewAll && onViewAll && (
              <button type="button" className="offers-deals-showcase-view-all" onClick={onViewAll}>
                View all offers
                <FaArrowRight />
              </button>
            )}
            <span className="offers-deals-showcase-dots" aria-hidden="true" />
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="offers-deals-showcase-loading">Loading offers...</div>
      ) : displayOffers.length === 0 ? (
        <div className="offers-deals-showcase-empty">No offers available at the moment.</div>
      ) : carousel ? (
        <div
          className="offers-deals-showcase-carousel"
          aria-label="Offers and deals slider"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          <div
            className="offers-deals-showcase-track"
            style={{ transform: `translateX(-${activeSlide * 100}%)` }}
          >
            {carouselSlides.map((slide, slideIndex) => (
              <div className="offers-deals-showcase-slide" key={`offer-slide-${slideIndex}`}>
                {slide.map((item, index) => (
                  <PremiumOfferCard
                    key={`${slideIndex}-${index}-${item.id || item.offerId || index}`}
                    item={item}
                    index={slideIndex * cardsPerSlide + index}
                    onOfferClick={onOfferClick}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="offers-deals-showcase-grid">
          {displayOffers.map((item, index) => (
            <PremiumOfferCard
              key={item.id || item.offerId || index}
              item={item}
              index={index}
              onOfferClick={onOfferClick}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default OffersDealsShowcase;
